<template>
  <div ref="mapRef" class="custom-map"></div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import { Loader } from "@googlemaps/js-api-loader";

const props = defineProps({
  lat: { type: Number, required: true },
  lng: { type: Number, required: true },
  logoUrl: { type: String, default: "" },
});

const mapRef = ref(null);
let map = null;
let marker = null;
let themeObserver = null;
let loaderPromise = null;

const API_KEY   = 'AIzaSyBSEN-wo__FMd5MeQUIkQYGFPuqZeHgVNY';
const MAP_ID_LT = 'a322bd3b07d6ae91fc71cb51'; 
const MAP_ID_DK = 'a322bd3b07d6ae9b2892b4c3 ';  

const lightMapStyle = [
  { "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }] },
  { "elementType": "labels.icon", "stylers": [{ "visibility": "off" }] },
  { "elementType": "labels.text.fill", "stylers": [{ "color": "#616161" }] },
  { "elementType": "labels.text.stroke", "stylers": [{ "color": "#f5f5f5" }] },
  { "featureType": "administrative.land_parcel", "elementType": "labels.text.fill", "stylers": [{ "color": "#bdbdbd" }] },
  { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#eeeeee" }] },
  { "featureType": "poi", "elementType": "labels.text.fill", "stylers": [{ "color": "#757575" }] },
  { "featureType": "poi.park", "elementType": "geometry", "stylers": [{ "color": "#e5e5e5" }] },
  { "featureType": "poi.park", "elementType": "labels.text.fill", "stylers": [{ "color": "#9e9e9e" }] },
  { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#ffffff" }] },
  { "featureType": "road.arterial", "elementType": "labels.text.fill", "stylers": [{ "color": "#757575" }] },
  { "featureType": "road.highway", "elementType": "geometry", "stylers": [{ "color": "#dadada" }] },
  { "featureType": "road.highway", "elementType": "labels.text.fill", "stylers": [{ "color": "#616161" }] },
  { "featureType": "road.local", "elementType": "labels.text.fill", "stylers": [{ "color": "#9e9e9e" }] },
  { "featureType": "transit.line", "elementType": "geometry", "stylers": [{ "color": "#e5e5e5" }] },
  { "featureType": "transit.station", "elementType": "geometry", "stylers": [{ "color": "#eeeeee" }] },
  { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#c9c9c9" }] },
  { "featureType": "water", "elementType": "labels.text.fill", "stylers": [{ "color": "#9e9e9e" }] }
];

const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#757575" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#2e2e2e" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#181818" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#383838" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#484848" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#505050" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f1f1f" }] },
  { featureType: "road.highway.controlled_access", elementType: "geometry", stylers: [{ color: "#4e4e4e" }] },
  { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f2f2f" }] },
  { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#3a3a3a" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] }
]; 

const getTheme = () =>
  document.documentElement.getAttribute("data-theme") || "light";
const getMapId = () => (getTheme() === "dark" ? MAP_ID_DK : MAP_ID_LT);

function createPinElement(logoUrl) {
  const el = document.createElement("div");
  el.className = "g-pin";
  el.innerHTML = `<img class="g-pin-logo" src="${logoUrl || ""}" alt="logo">`;
  return el;
}
const getPinUrl = () =>
  getTheme() === "dark"
    ? "/images/pin-hyper-dark.png"
    : "/images/pin-hyper.png";

function createMap({ center, zoom }) {
  map = new google.maps.Map(mapRef.value, {
    center,
    zoom,
    disableDefaultUI: true,
    styles: getTheme() === "dark" ? darkMapStyle : lightMapStyle,
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    isFractionalZoomEnabled: true,
  });

  marker = new google.maps.Marker({
    map,
    position: center,
    title: "Офис",
    icon: {
      url: getPinUrl(),
      scaledSize: new google.maps.Size(48, 60),
      anchor: new google.maps.Point(24, 60),
    },
  });
}

async function rebuildMap() {
  if (!mapRef.value) return;
  const center =
    map?.getCenter?.() || new google.maps.LatLng(props.lat, props.lng);
  const zoom = map?.getZoom?.() ?? 16;

  marker?.setMap?.(null);
  marker = null;
  map = null;

  createMap({ center, zoom, mapId: getMapId() });
}

async function init() {
  loaderPromise ||= new Loader({
    apiKey: API_KEY,
    version: "weekly",
    libraries: ["marker", "places"],
  }).load();

  await loaderPromise;

  createMap({
    center: new google.maps.LatLng(props.lat, props.lng),
    zoom: 16,
    mapId: getMapId(),
  });

  themeObserver = new MutationObserver(() => {
    rebuildMap().catch(console.error);
  });
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
}

onMounted(() => {
  if (!API_KEY) console.warn("⚠️ VITE_GOOGLE_MAPS_API_KEY не задан");
  if (!MAP_ID_LT || !MAP_ID_DK) {
    console.warn("⚠️ Нужны оба Map ID: VITE_GOOGLE_MAPS_LIGHT_MAP_ID и VITE_GOOGLE_MAPS_DARK_MAP_ID");
  }

  console.log("🌓 Current theme:", getTheme());
  console.log("🎨 MapId selected:", getMapId());

  init().catch((e) => console.error("Map init error:", e));
});

onBeforeUnmount(() => {
  themeObserver?.disconnect();
  marker?.setMap?.(null);
  marker = null;
  map = null;
});

watch(() => [props.lat, props.lng], () => {
  if (!map || !marker) return;
  const pos = new google.maps.LatLng(props.lat, props.lng);
  map.setCenter(pos);
  if ("position" in marker) marker.position = pos; 
  else marker.setPosition(pos);                    
});

watch(() => props.logoUrl, (val) => {
  const Adv = google.maps?.marker?.AdvancedMarkerElement;
  if (Adv && marker && marker.content?.querySelector) {
    const img = marker.content.querySelector(".g-pin-logo");
    if (img) img.src = val || "";
  }
});
</script>

<style scoped>
.custom-map {
  width: 740px;
  height: 300px;
  overflow: hidden;
}

:global(.g-pin) {
  position: relative;
  width: 48px;
  height: 60px;
  background: url("../assets/pin-hyper.png") no-repeat center / contain;
  transform: translate(-50%, -100%);
}

:global(.g-pin-logo) {
  position: absolute;
  left: 50%;
  top: 2px;              
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: #fff;
  object-fit: cover;
  z-index: 2;
}
</style>